/**
 * CommunityManagement - Admin Panel
 * Platform-wide community hub management
 * CSS prefix: acm- (admin community management)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from './ui/Toast';
import { api } from '../utils/api';
import './CommunityManagement.css';

/* =============================================
   SVG ICON COMPONENTS (Feather/Lucide style)
   ============================================= */

const Icons = {
  Users: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  MessageSquare: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  Package: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M16.5 9.4l-9-5.19M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
      <polyline points="3.27,6.96 12,12.01 20.73,6.96" /><line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),
  Flag: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" />
    </svg>
  ),
  Settings: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  BarChart: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" />
    </svg>
  ),
  Search: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  Plus: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  Edit: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  Trash: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  ),
  CheckCircle: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  XCircle: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  ),
  AlertTriangle: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  Star: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  TrendingUp: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  Info: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
  X: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  RefreshCw: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  ),
  Eye: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  ),
  EyeOff: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ),
  ChevronLeft: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  ChevronRight: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
};

/* =============================================
   UTILITY FUNCTIONS
   ============================================= */

const formatNumber = (num) => (Number(num) || 0).toLocaleString();

const formatCurrency = (amount) => {
  const num = Number(amount) || 0;
  return '\u20A6' + num.toLocaleString('en-NG', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-NG', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/* =============================================
   MAIN COMPONENT
   ============================================= */

const CommunityManagement = () => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dashboard state
  const [dashboardData, setDashboardData] = useState(null);

  // Communities state
  const [communities, setCommunities] = useState([]);
  const [communitySearch, setCommunitySearch] = useState('');
  const [communityType, setCommunityType] = useState('all');
  const [communityStatus, setCommunityStatus] = useState('all');
  const [communityPage, setCommunityPage] = useState(1);
  const [communityTotal, setCommunityTotal] = useState(0);

  // Suppliers state
  const [suppliers, setSuppliers] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierStatus, setSupplierStatus] = useState('all');
  const [supplierPage, setSupplierPage] = useState(1);
  const [supplierTotal, setSupplierTotal] = useState(0);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  // Reports state
  const [reports, setReports] = useState([]);
  const [reportStatus, setReportStatus] = useState('PENDING');
  const [reportPage, setReportPage] = useState(1);
  const [reportTotal, setReportTotal] = useState(0);

  // Settings state
  const [settings, setSettings] = useState(null);
  const [availableTiers, setAvailableTiers] = useState([]);
  const [profanityWordListText, setProfanityWordListText] = useState('');

  // Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('7d');

  // Payments state
  const [paymentsData, setPaymentsData] = useState(null);
  const [paymentsPeriod, setPaymentsPeriod] = useState('30d');

  // Payouts state
  const [payouts, setPayouts] = useState([]);
  const [payoutsStatus, setPayoutsStatus] = useState('all');
  const [payoutsPage, setPayoutsPage] = useState(1);
  const [payoutsTotal, setPayoutsTotal] = useState(0);
  const [revenueSummary, setRevenueSummary] = useState(null);
  const [processingPayouts, setProcessingPayouts] = useState(false);
  const [selectedPayouts, setSelectedPayouts] = useState([]);
  // Month selector for payout generation (default to current month)
  const [payoutYear, setPayoutYear] = useState(new Date().getFullYear());
  const [payoutMonth, setPayoutMonth] = useState(new Date().getMonth()); // 0-indexed

  // Brand Community Tiers state
  const [tierModalData, setTierModalData] = useState(null);
  const [tierStats, setTierStats] = useState(null);
  const [tierLoading, setTierLoading] = useState(false);

  // Saving state
  const [saving, setSaving] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [modalData, setModalData] = useState(null);

  /* =============================================
     DATA FETCHING
     ============================================= */

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.get('/admin/community/dashboard');
      setDashboardData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCommunities = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: communityPage,
        limit: 10,
        ...(communityType !== 'all' && { type: communityType }),
        ...(communityStatus !== 'all' && { status: communityStatus }),
        ...(communitySearch && { search: communitySearch })
      });
      const data = await api.get(`/admin/community/communities?${params}`);
      setCommunities(data.communities || []);
      setCommunityTotal(data.pagination?.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [communityPage, communityType, communityStatus, communitySearch]);

  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: supplierPage,
        limit: 10,
        ...(supplierStatus !== 'all' && { status: supplierStatus }),
        ...(supplierSearch && { search: supplierSearch })
      });
      const data = await api.get(`/admin/community/suppliers?${params}`);
      setSuppliers(data.suppliers || []);
      setSupplierTotal(data.pagination?.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [supplierPage, supplierStatus, supplierSearch]);

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: reportPage,
        limit: 10,
        status: reportStatus
      });
      const data = await api.get(`/admin/community/reports?${params}`);
      setReports(data.reports || []);
      setReportTotal(data.pagination?.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [reportPage, reportStatus]);

  const fetchTiers = useCallback(async () => {
    try {
      // Use same pattern as TierManagement.js
      const response = await api.get('/admin/tiers');

      let tiersData = [];
      // Handle different response formats (same as TierManagement.js)
      if (response.status === 'success' && response.message && Array.isArray(response.message)) {
        tiersData = response.message;
      } else if (response.data && Array.isArray(response.data)) {
        tiersData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        tiersData = response.data.data;
      } else if (response.data?.message && Array.isArray(response.data.message)) {
        tiersData = response.data.message;
      } else if (response.tiers && Array.isArray(response.tiers)) {
        tiersData = response.tiers;
      }

      // Sort by sortOrder like TierManagement.js does
      tiersData.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

      // Extract tier names (field is 'tier' in Prisma Tier model)
      const tierNames = tiersData
        .map(t => t.tier || t.name)
        .filter(Boolean);

      if (tierNames.length > 0) {
        setAvailableTiers(tierNames);
      } else {
        throw new Error('No tiers found');
      }
    } catch (err) {
      console.error('Failed to fetch tiers:', err);
      setError('Failed to load tiers: ' + err.message);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch tiers first
      await fetchTiers();
      // Then fetch settings
      const settingsData = await api.get('/admin/community/settings');
      const configData = settingsData.config || settingsData;
      setSettings(configData);
      // Initialize profanity word list text
      if (Array.isArray(configData.profanityWordList)) {
        setProfanityWordListText(configData.profanityWordList.join(', '));
      } else {
        setProfanityWordListText('');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchTiers]);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.get(`/admin/community/analytics?period=${analyticsPeriod}`);
      setAnalyticsData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [analyticsPeriod]);

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.get(`/admin/community/payments?period=${paymentsPeriod}`);
      setPaymentsData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [paymentsPeriod]);

  const fetchPayouts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: payoutsPage,
        limit: 20,
        ...(payoutsStatus !== 'all' && { status: payoutsStatus })
      });
      const [payoutsData, summaryData] = await Promise.all([
        api.get(`/admin/community/payouts?${params}`),
        api.get('/admin/community/payouts/revenue-summary')
      ]);
      setPayouts(payoutsData.payouts || []);
      setPayoutsTotal(payoutsData.total || 0);
      setRevenueSummary(summaryData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [payoutsPage, payoutsStatus]);

  useEffect(() => {
    if (activeTab === 'dashboard') fetchDashboard();
    if (activeTab === 'communities') fetchCommunities();
    if (activeTab === 'suppliers') fetchSuppliers();
    if (activeTab === 'moderation') fetchReports();
    if (activeTab === 'settings') fetchSettings();
    if (activeTab === 'analytics') fetchAnalytics();
    if (activeTab === 'payments') fetchPayments();
    if (activeTab === 'payouts') fetchPayouts();
  }, [activeTab, fetchDashboard, fetchCommunities, fetchSuppliers, fetchReports, fetchSettings, fetchAnalytics, fetchPayments, fetchPayouts]);

  /* =============================================
     ACTIONS
     ============================================= */

  const handleVerifySupplier = async (id) => {
    try {
      await api.put(`/admin/community/suppliers/${id}/verify`);
      fetchSuppliers();
      toast.success('Supplier verified successfully');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRejectSupplier = async (id, reason) => {
    try {
      await api.put(`/admin/community/suppliers/${id}/reject`, { reason });
      fetchSuppliers();
      toast.success('Supplier rejected');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSuspendSupplier = async (id, reason) => {
    try {
      await api.put(`/admin/community/suppliers/${id}/suspend`, { reason });
      fetchSuppliers();
      toast.success('Supplier suspended');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleModerationAction = async (reportId, action, reason = '') => {
    try {
      await api.post('/admin/community/moderation/action', { reportId, action, reason });
      fetchReports();
      fetchDashboard();
      toast.success('Moderation action completed');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSaveSettings = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const response = await api.put('/admin/community/settings', settings);
      // Update local settings with response to ensure sync
      if (response.config) {
        setSettings(response.config);
      }
      toast.success('Settings saved successfully');
    } catch (err) {
      console.error('Save settings error:', err);
      toast.error(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCommunity = async (formData) => {
    try {
      await api.post('/admin/community/communities', formData);
      setShowModal(false);
      fetchCommunities();
      toast.success('Community created successfully');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteCommunity = async (id) => {
    if (!window.confirm('Are you sure you want to delete this community? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/community/communities/${id}`);
      fetchCommunities();
      toast.success('Community deleted');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEditCommunity = (community) => {
    setModalType('editCommunity');
    setModalData(community);
    setShowModal(true);
  };

  // Brand Community Tier Management
  const handleOpenTierModal = async (community) => {
    setTierModalData(community);
    setTierLoading(true);
    try {
      const stats = await api.get(`/admin/community/brand/${community.storeId}/tier-stats`);
      setTierStats(stats);
    } catch (err) {
      toast.error('Failed to load tier stats');
      setTierStats(null);
    } finally {
      setTierLoading(false);
    }
  };

  const handleCloseTierModal = () => {
    setTierModalData(null);
    setTierStats(null);
  };

  const handleRecalculateTiers = async () => {
    if (!tierModalData?.storeId) return;
    setTierLoading(true);
    try {
      const result = await api.post(`/admin/community/brand/${tierModalData.storeId}/recalculate-tiers`);
      toast.success(`Recalculated tiers: ${result.results?.updated || 0} updated, ${result.results?.upgraded || 0} upgraded`);
      // Refresh stats
      const stats = await api.get(`/admin/community/brand/${tierModalData.storeId}/tier-stats`);
      setTierStats(stats);
    } catch (err) {
      toast.error(err.message || 'Failed to recalculate tiers');
    } finally {
      setTierLoading(false);
    }
  };

  const handleRecalculateAllTiers = async () => {
    if (!window.confirm('Recalculate tiers for ALL brand communities? This may take a while.')) return;
    setTierLoading(true);
    try {
      const result = await api.post('/admin/community/brand/recalculate-all-tiers');
      toast.success(`Processed ${result.processed || 0} communities`);
    } catch (err) {
      toast.error(err.message || 'Failed to recalculate all tiers');
    } finally {
      setTierLoading(false);
    }
  };

  const handleUpdateCommunity = async (formData) => {
    try {
      await api.put(`/admin/community/communities/${modalData.id}`, formData);
      setShowModal(false);
      setModalData(null);
      fetchCommunities();
      toast.success('Community updated successfully');
    } catch (err) {
      toast.error(err.message);
    }
  };

  /* =============================================
     RENDER FUNCTIONS
     ============================================= */

  const renderDashboard = () => {
    if (!dashboardData) return <div className="acm-loading">Loading...</div>;

    const { stats, growth, recentPosts, topCommunities } = dashboardData;

    return (
      <div className="acm-dashboard">
        {/* Stats Cards */}
        <div className="acm-stats-grid">
          <div className="acm-stat-card">
            <div className="acm-stat-icon acm-stat-icon-blue">
              <Icons.Users size={24} />
            </div>
            <div className="acm-stat-content">
              <span className="acm-stat-value">{formatNumber(stats.totalMembers)}</span>
              <span className="acm-stat-label">Total Members</span>
              <span className="acm-stat-trend">+{formatNumber(growth.newMembers)} this week</span>
            </div>
          </div>

          <div className="acm-stat-card">
            <div className="acm-stat-icon acm-stat-icon-green">
              <Icons.MessageSquare size={24} />
            </div>
            <div className="acm-stat-content">
              <span className="acm-stat-value">{formatNumber(stats.totalPosts)}</span>
              <span className="acm-stat-label">Total Posts</span>
              <span className="acm-stat-trend">+{formatNumber(growth.newPosts)} this week</span>
            </div>
          </div>

          <div className="acm-stat-card">
            <div className="acm-stat-icon acm-stat-icon-purple">
              <Icons.Package size={24} />
            </div>
            <div className="acm-stat-content">
              <span className="acm-stat-value">{formatNumber(stats.totalSuppliers)}</span>
              <span className="acm-stat-label">Verified Suppliers</span>
              <span className="acm-stat-trend">{formatNumber(stats.pendingSuppliers)} pending</span>
            </div>
          </div>

          <div className="acm-stat-card">
            <div className="acm-stat-icon acm-stat-icon-orange">
              <Icons.Flag size={24} />
            </div>
            <div className="acm-stat-content">
              <span className="acm-stat-value">{formatNumber(stats.pendingReports)}</span>
              <span className="acm-stat-label">Pending Reports</span>
              <span className="acm-stat-trend">Needs attention</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="acm-quick-actions">
          <h3>Quick Actions</h3>
          <div className="acm-actions-grid">
            <button className="acm-action-btn" onClick={() => { setActiveTab('suppliers'); setSupplierStatus('PENDING'); }}>
              <Icons.CheckCircle size={20} />
              Review Suppliers ({stats.pendingSuppliers})
            </button>
            <button className="acm-action-btn" onClick={() => setActiveTab('moderation')}>
              <Icons.Flag size={20} />
              Moderate Reports ({stats.pendingReports})
            </button>
            <button className="acm-action-btn" onClick={() => { setShowModal(true); setModalType('create-community'); }}>
              <Icons.Plus size={20} />
              Create Community
            </button>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="acm-dashboard-columns">
          {/* Top Communities */}
          <div className="acm-dashboard-section">
            <h3>Top Communities</h3>
            <div className="acm-mini-table">
              {topCommunities.map(c => (
                <div key={c.id} className="acm-mini-row">
                  <span className="acm-mini-name">{c.name}</span>
                  <span className="acm-mini-stat">{formatNumber(c.memberCount)} members</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Posts */}
          <div className="acm-dashboard-section">
            <h3>Recent Posts</h3>
            <div className="acm-mini-table">
              {recentPosts.map(p => (
                <div key={p.id} className="acm-mini-row">
                  <span className="acm-mini-name">{p.title?.slice(0, 40) || 'Untitled'}...</span>
                  <span className="acm-mini-stat">in {p.community?.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCommunities = () => (
    <div className="acm-section">
      <div className="acm-section-header">
        <h2>Communities</h2>
        <div className="acm-header-actions">
          <button
            className="acm-btn acm-btn-secondary"
            onClick={handleRecalculateAllTiers}
            disabled={tierLoading}
            title="Recalculate customer tiers for all brand communities"
          >
            <Icons.RefreshCw size={16} />
            Recalc All Tiers
          </button>
          <button className="acm-btn acm-btn-primary" onClick={() => { setShowModal(true); setModalType('create-community'); }}>
            <Icons.Plus size={18} /> Create Community
          </button>
        </div>
      </div>

      <div className="acm-filters">
        <div className="acm-search">
          <Icons.Search size={18} />
          <input
            type="text"
            placeholder="Search communities..."
            value={communitySearch}
            onChange={(e) => setCommunitySearch(e.target.value)}
          />
        </div>
        <select value={communityType} onChange={(e) => setCommunityType(e.target.value)}>
          <option value="all">All Types</option>
          <option value="MASTERMIND_NICHE">Niche Mastermind</option>
          <option value="MASTERMIND_CITY">City Mastermind</option>
          <option value="MASTERMIND_STAGE">Stage Mastermind</option>
          <option value="BRAND_COMMUNITY">Brand Community</option>
        </select>
        <select value={communityStatus} onChange={(e) => setCommunityStatus(e.target.value)}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <button className="acm-btn acm-btn-secondary" onClick={fetchCommunities}>
          <Icons.RefreshCw size={18} />
        </button>
      </div>

      <div className="acm-table-container">
        <table className="acm-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Status</th>
              <th>Members</th>
              <th>Posts</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {communities.map(c => (
              <tr key={c.id}>
                <td>
                  <div className="acm-cell-name">
                    <strong>{c.name}</strong>
                    <span className="acm-cell-slug">/{c.slug}</span>
                  </div>
                </td>
                <td><span className="acm-badge acm-badge-type">{c.type}</span></td>
                <td>
                  <span className={`acm-badge ${c.isActive ? 'acm-badge-verified' : 'acm-badge-suspended'}`}>
                    {c.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {c.isFeatured && (
                    <span className="acm-badge acm-badge-tier" style={{ marginLeft: '0.25rem' }}>Featured</span>
                  )}
                </td>
                <td>{formatNumber(c.memberCount)}</td>
                <td>{formatNumber(c.postCount)}</td>
                <td>{formatDate(c.createdAt)}</td>
                <td>
                  <div className="acm-actions">
                    {c.type === 'BRAND_COMMUNITY' && c.storeId && (
                      <button
                        className="acm-action-icon"
                        title="Manage Tiers"
                        onClick={() => handleOpenTierModal(c)}
                      >
                        <Icons.Star size={16} />
                      </button>
                    )}
                    <button className="acm-action-icon" title="Edit" onClick={() => handleEditCommunity(c)}>
                      <Icons.Edit size={16} />
                    </button>
                    <button className="acm-action-icon acm-action-danger" title="Delete" onClick={() => handleDeleteCommunity(c.id)}>
                      <Icons.Trash size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {communityTotal > 10 && (
        <div className="acm-pagination">
          <button disabled={communityPage === 1} onClick={() => setCommunityPage(p => p - 1)}>
            <Icons.ChevronLeft size={18} />
          </button>
          <span>Page {communityPage} of {Math.ceil(communityTotal / 10)}</span>
          <button disabled={communityPage >= Math.ceil(communityTotal / 10)} onClick={() => setCommunityPage(p => p + 1)}>
            <Icons.ChevronRight size={18} />
          </button>
        </div>
      )}
    </div>
  );

  const renderSuppliers = () => (
    <div className="acm-section">
      <div className="acm-section-header">
        <h2>Suppliers</h2>
      </div>

      <div className="acm-filters">
        <div className="acm-search">
          <Icons.Search size={18} />
          <input
            type="text"
            placeholder="Search suppliers..."
            value={supplierSearch}
            onChange={(e) => setSupplierSearch(e.target.value)}
          />
        </div>
        <select value={supplierStatus} onChange={(e) => setSupplierStatus(e.target.value)}>
          <option value="all">All Status</option>
          <option value="PENDING">Pending</option>
          <option value="VERIFIED">Verified</option>
          <option value="SUSPENDED">Suspended</option>
        </select>
        <button className="acm-btn acm-btn-secondary" onClick={fetchSuppliers}>
          <Icons.RefreshCw size={18} />
        </button>
      </div>

      <div className="acm-table-container">
        <table className="acm-table">
          <thead>
            <tr>
              <th>Business Name</th>
              <th>Email</th>
              <th>Location</th>
              <th>Status</th>
              <th>Tier</th>
              <th>Rating</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map(s => (
              <tr key={s.id}>
                <td>
                  <div className="acm-cell-name">
                    <strong>{s.businessName}</strong>
                    <span className="acm-cell-slug">/{s.slug}</span>
                  </div>
                </td>
                <td>{s.email}</td>
                <td>{s.city}, {s.state}</td>
                <td>
                  <div className="acm-status-badges">
                    <span className={`acm-badge acm-badge-${s.status.toLowerCase()}`}>
                      {s.status}
                    </span>
                    {s.verificationPaid && (
                      <span className="acm-badge acm-badge-paid">Paid</span>
                    )}
                    {s.emailVerified && !s.verificationPaid && (
                      <span className="acm-badge acm-badge-email">Email ✓</span>
                    )}
                  </div>
                </td>
                <td><span className="acm-badge acm-badge-tier">{s.tier}</span></td>
                <td>
                  <div className="acm-rating">
                    <Icons.Star size={14} />
                    {parseFloat(s.rating || 0).toFixed(1)} ({s.reviewCount || 0})
                  </div>
                </td>
                <td>
                  <div className="acm-actions">
                    {s.status === 'PENDING' && (
                      <>
                        <button className="acm-action-icon acm-action-success" title="Verify" onClick={() => handleVerifySupplier(s.id)}>
                          <Icons.CheckCircle size={16} />
                        </button>
                        <button className="acm-action-icon acm-action-danger" title="Reject" onClick={() => handleRejectSupplier(s.id, 'Does not meet requirements')}>
                          <Icons.XCircle size={16} />
                        </button>
                      </>
                    )}
                    {s.status === 'VERIFIED' && (
                      <button className="acm-action-icon acm-action-warning" title="Suspend" onClick={() => handleSuspendSupplier(s.id, 'Policy violation')}>
                        <Icons.EyeOff size={16} />
                      </button>
                    )}
                    <button className="acm-action-icon" title="View" onClick={() => setSelectedSupplier(s)}>
                      <Icons.Eye size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {supplierTotal > 10 && (
        <div className="acm-pagination">
          <button disabled={supplierPage === 1} onClick={() => setSupplierPage(p => p - 1)}>
            <Icons.ChevronLeft size={18} />
          </button>
          <span>Page {supplierPage} of {Math.ceil(supplierTotal / 10)}</span>
          <button disabled={supplierPage >= Math.ceil(supplierTotal / 10)} onClick={() => setSupplierPage(p => p + 1)}>
            <Icons.ChevronRight size={18} />
          </button>
        </div>
      )}

      {/* Supplier Detail Modal */}
      {selectedSupplier && (
        <div className="acm-modal-overlay" onClick={() => setSelectedSupplier(null)}>
          <div className="acm-modal acm-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="acm-modal-header">
              <h2>Supplier Details</h2>
              <button className="acm-modal-close" onClick={() => setSelectedSupplier(null)}>
                <Icons.X size={20} />
              </button>
            </div>
            <div className="acm-modal-content">
              <div className="acm-supplier-detail">
                <div className="acm-supplier-detail-header">
                  {selectedSupplier.logo && (
                    <img src={selectedSupplier.logo} alt="" className="acm-supplier-logo" />
                  )}
                  <div>
                    <h3>{selectedSupplier.businessName}</h3>
                    <span className={`acm-badge acm-badge-${selectedSupplier.status.toLowerCase()}`}>
                      {selectedSupplier.status}
                    </span>
                    <span className="acm-badge acm-badge-tier">{selectedSupplier.tier}</span>
                  </div>
                </div>

                <div className="acm-supplier-detail-grid">
                  <div className="acm-detail-item">
                    <label>Email</label>
                    <span>{selectedSupplier.email}</span>
                  </div>
                  <div className="acm-detail-item">
                    <label>Phone</label>
                    <span>{selectedSupplier.phone || '-'}</span>
                  </div>
                  <div className="acm-detail-item">
                    <label>Location</label>
                    <span>{selectedSupplier.city}, {selectedSupplier.state}, {selectedSupplier.country}</span>
                  </div>
                  <div className="acm-detail-item">
                    <label>Slug</label>
                    <span>/{selectedSupplier.slug}</span>
                  </div>
                  <div className="acm-detail-item">
                    <label>Rating</label>
                    <span>{parseFloat(selectedSupplier.rating || 0).toFixed(1)} ({selectedSupplier.reviewCount || 0} reviews)</span>
                  </div>
                  <div className="acm-detail-item">
                    <label>Products</label>
                    <span>{selectedSupplier._count?.products || 0}</span>
                  </div>
                  <div className="acm-detail-item">
                    <label>Created</label>
                    <span>{formatDate(selectedSupplier.createdAt)}</span>
                  </div>
                  {selectedSupplier.description && (
                    <div className="acm-detail-item acm-detail-full">
                      <label>Description</label>
                      <p>{selectedSupplier.description}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderModeration = () => (
    <div className="acm-section">
      <div className="acm-section-header">
        <h2>Moderation Queue</h2>
      </div>

      <div className="acm-filters">
        <select value={reportStatus} onChange={(e) => setReportStatus(e.target.value)}>
          <option value="PENDING">Pending</option>
          <option value="RESOLVED">Resolved</option>
          <option value="all">All</option>
        </select>
        <button className="acm-btn acm-btn-secondary" onClick={fetchReports}>
          <Icons.RefreshCw size={18} />
        </button>
      </div>

      {reports.length === 0 ? (
        <div className="acm-empty">
          <Icons.CheckCircle size={48} />
          <p>No pending reports</p>
        </div>
      ) : (
        <div className="acm-reports-list">
          {reports.map(r => (
            <div key={r.id} className="acm-report-card">
              <div className="acm-report-header">
                <span className="acm-report-type">{r.type}</span>
                <span className="acm-report-date">{formatDate(r.createdAt)}</span>
              </div>
              <div className="acm-report-content">
                <p><strong>Reported by:</strong> {r.reporterName || 'Unknown'}</p>
                <p><strong>Reason:</strong> {r.reason}</p>
                {r.targetPost && <p><strong>Post:</strong> {r.targetPost.title}</p>}
                {r.targetComment && <p><strong>Comment:</strong> {r.targetComment.content?.slice(0, 100)}...</p>}
                {r.details && <p><strong>Details:</strong> {r.details}</p>}
              </div>
              {r.status === 'PENDING' && (
                <div className="acm-report-actions">
                  <button className="acm-btn acm-btn-sm acm-btn-secondary" onClick={() => handleModerationAction(r.id, 'DISMISS')}>
                    Dismiss
                  </button>
                  <button className="acm-btn acm-btn-sm acm-btn-warning" onClick={() => handleModerationAction(r.id, 'WARN')}>
                    Warn User
                  </button>
                  <button className="acm-btn acm-btn-sm acm-btn-danger" onClick={() => handleModerationAction(r.id, 'HIDE_CONTENT')}>
                    Hide Content
                  </button>
                  <button className="acm-btn acm-btn-sm acm-btn-danger" onClick={() => handleModerationAction(r.id, 'BAN_MEMBER')}>
                    Ban Member
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderAnalytics = () => {
    const periods = [
      { value: '24h', label: 'Last 24 Hours' },
      { value: '7d', label: 'Last 7 Days' },
      { value: '30d', label: 'Last 30 Days' },
      { value: '90d', label: 'Last 90 Days' }
    ];

    return (
      <div className="acm-analytics">
        {/* Period Selector */}
        <div className="acm-analytics-header">
          <h2>Community Analytics</h2>
          <div className="acm-period-selector">
            {periods.map(p => (
              <button
                key={p.value}
                className={`acm-period-btn ${analyticsPeriod === p.value ? 'active' : ''}`}
                onClick={() => setAnalyticsPeriod(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {!analyticsData ? (
          <div className="acm-loading">Loading analytics...</div>
        ) : (
          <>
            {/* Key Metrics */}
            <div className="acm-analytics-metrics">
              <div className="acm-metric-card">
                <div className="acm-metric-icon acm-metric-members">
                  <Icons.Users size={24} />
                </div>
                <div className="acm-metric-content">
                  <span className="acm-metric-value">{analyticsData.metrics?.newMembers || 0}</span>
                  <span className="acm-metric-label">New Members</span>
                </div>
              </div>
              <div className="acm-metric-card">
                <div className="acm-metric-icon acm-metric-posts">
                  <Icons.MessageSquare size={24} />
                </div>
                <div className="acm-metric-content">
                  <span className="acm-metric-value">{analyticsData.metrics?.newPosts || 0}</span>
                  <span className="acm-metric-label">New Posts</span>
                </div>
              </div>
              <div className="acm-metric-card">
                <div className="acm-metric-icon acm-metric-comments">
                  <Icons.MessageSquare size={24} />
                </div>
                <div className="acm-metric-content">
                  <span className="acm-metric-value">{analyticsData.metrics?.newComments || 0}</span>
                  <span className="acm-metric-label">New Comments</span>
                </div>
              </div>
              <div className="acm-metric-card">
                <div className="acm-metric-icon acm-metric-active">
                  <Icons.TrendingUp size={24} />
                </div>
                <div className="acm-metric-content">
                  <span className="acm-metric-value">{analyticsData.metrics?.activeUsers || 0}</span>
                  <span className="acm-metric-label">Active Users</span>
                </div>
              </div>
            </div>

            {/* Top Communities & Top Posters */}
            <div className="acm-analytics-grid">
              {/* Top Communities */}
              <div className="acm-analytics-section">
                <h3>Top Communities</h3>
                <div className="acm-analytics-list">
                  {(analyticsData.topCommunities || []).map((community, idx) => (
                    <div key={community.id} className="acm-analytics-item">
                      <span className="acm-analytics-rank">{idx + 1}</span>
                      <div className="acm-analytics-item-info">
                        <span className="acm-analytics-item-name">{community.name}</span>
                        <span className="acm-analytics-item-meta">
                          {community.memberCount} members · {community.postCount} posts
                        </span>
                      </div>
                    </div>
                  ))}
                  {(!analyticsData.topCommunities || analyticsData.topCommunities.length === 0) && (
                    <div className="acm-analytics-empty">No community data yet</div>
                  )}
                </div>
              </div>

              {/* Top Posters */}
              <div className="acm-analytics-section">
                <h3>Top Contributors</h3>
                <div className="acm-analytics-list">
                  {(analyticsData.topPosters || []).map((member, idx) => (
                    <div key={member.id} className="acm-analytics-item">
                      <span className="acm-analytics-rank">{idx + 1}</span>
                      <div className="acm-analytics-item-info">
                        <span className="acm-analytics-item-name">{member.name || 'Unknown'}</span>
                        <span className="acm-analytics-item-meta">
                          {member.postCount || 0} posts · {member.commentCount || 0} comments
                        </span>
                      </div>
                    </div>
                  ))}
                  {(!analyticsData.topPosters || analyticsData.topPosters.length === 0) && (
                    <div className="acm-analytics-empty">No contributor data yet</div>
                  )}
                </div>
              </div>
            </div>

            {/* Engagement Summary */}
            <div className="acm-analytics-summary">
              <h3>Engagement Summary</h3>
              <div className="acm-summary-stats">
                <div className="acm-summary-stat">
                  <span className="acm-summary-label">Posts per Day</span>
                  <span className="acm-summary-value">
                    {analyticsPeriod === '24h'
                      ? analyticsData.metrics?.newPosts || 0
                      : ((analyticsData.metrics?.newPosts || 0) / (analyticsPeriod === '7d' ? 7 : analyticsPeriod === '30d' ? 30 : 90)).toFixed(1)}
                  </span>
                </div>
                <div className="acm-summary-stat">
                  <span className="acm-summary-label">Comments per Post</span>
                  <span className="acm-summary-value">
                    {analyticsData.metrics?.newPosts > 0
                      ? ((analyticsData.metrics?.newComments || 0) / analyticsData.metrics.newPosts).toFixed(1)
                      : '0'}
                  </span>
                </div>
                <div className="acm-summary-stat">
                  <span className="acm-summary-label">Member Growth Rate</span>
                  <span className="acm-summary-value">
                    {analyticsData.metrics?.newMembers || 0} / {analyticsPeriod}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // Settings sub-tab state
  const [settingsTab, setSettingsTab] = useState('features');

  const renderPayments = () => {
    const periods = [
      { value: '7d', label: 'Last 7 Days' },
      { value: '30d', label: 'Last 30 Days' },
      { value: '90d', label: 'Last 90 Days' },
      { value: 'all', label: 'All Time' }
    ];

    const handleBackfillPayments = async () => {
      try {
        const result = await api.post('/admin/community/payments/backfill');
        toast.success(result.message || 'Backfill complete');
        fetchPayments();
      } catch (err) {
        toast.error(err.message || 'Failed to backfill payments');
      }
    };

    return (
      <div className="acm-payments">
        {/* Header */}
        <div className="acm-payments-header">
          <h2>Payments & Revenue</h2>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <button
              className="acm-btn acm-btn-secondary acm-btn-sm"
              onClick={handleBackfillPayments}
              title="Import existing supplier payments that weren't recorded"
            >
              <Icons.RefreshCw size={16} />
              Sync Past Payments
            </button>
            <div className="acm-period-selector">
              {periods.map(p => (
                <button
                  key={p.value}
                  className={`acm-period-btn ${paymentsPeriod === p.value ? 'active' : ''}`}
                  onClick={() => setPaymentsPeriod(p.value)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {!paymentsData ? (
          <div className="acm-loading">Loading payments data...</div>
        ) : (
          <>
            {/* Show message if tables not migrated */}
            {paymentsData.message && (
              <div className="acm-info-banner" style={{
                padding: '1rem',
                background: '#fef3c7',
                border: '1px solid #fcd34d',
                borderRadius: '8px',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <Icons.AlertTriangle size={20} style={{ color: '#d97706' }} />
                <span style={{ color: '#92400e', fontSize: '0.875rem' }}>{paymentsData.message}</span>
              </div>
            )}

            {/* Revenue Summary Cards */}
            <div className="acm-revenue-grid">
              <div className="acm-revenue-card acm-revenue-total">
                <div className="acm-revenue-icon">
                  <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                </div>
                <div className="acm-revenue-content">
                  <span className="acm-revenue-amount">{formatCurrency(paymentsData.summary?.totalRevenue || 0)}</span>
                  <span className="acm-revenue-label">Total Revenue</span>
                </div>
              </div>

              <div className="acm-revenue-card">
                <div className="acm-revenue-icon acm-revenue-supplier">
                  <Icons.Package size={24} />
                </div>
                <div className="acm-revenue-content">
                  <span className="acm-revenue-amount">{formatCurrency(paymentsData.summary?.supplierRevenue || 0)}</span>
                  <span className="acm-revenue-label">Supplier Fees</span>
                  <span className="acm-revenue-detail">{paymentsData.summary?.supplierCount || 0} suppliers</span>
                </div>
              </div>

              <div className="acm-revenue-card">
                <div className="acm-revenue-icon acm-revenue-community">
                  <Icons.Users size={24} />
                </div>
                <div className="acm-revenue-content">
                  <span className="acm-revenue-amount">{formatCurrency(paymentsData.summary?.communityRevenue || 0)}</span>
                  <span className="acm-revenue-label">Community Fees</span>
                  <span className="acm-revenue-detail">{paymentsData.summary?.communityCount || 0} paid communities</span>
                </div>
              </div>

              <div className="acm-revenue-card">
                <div className="acm-revenue-icon acm-revenue-pending">
                  <Icons.AlertTriangle size={24} />
                </div>
                <div className="acm-revenue-content">
                  <span className="acm-revenue-amount">{formatCurrency(paymentsData.summary?.pendingPayouts || 0)}</span>
                  <span className="acm-revenue-label">Pending Payouts</span>
                  <span className="acm-revenue-detail">To merchants</span>
                </div>
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="acm-payments-columns">
              {/* Recent Payments */}
              <div className="acm-payments-section">
                <h3>Recent Payments Received</h3>
                {(paymentsData.recentPayments || []).length === 0 ? (
                  <div className="acm-empty-small">No payments yet</div>
                ) : (
                  <div className="acm-payments-list">
                    {(paymentsData.recentPayments || []).map(payment => (
                      <div key={payment.id} className="acm-payment-row">
                        <div className="acm-payment-info">
                          <span className="acm-payment-type">
                            {payment.type === 'SUPPLIER_VERIFICATION' && 'Supplier Verification'}
                            {payment.type === 'SUPPLIER_FEATURED' && 'Featured Supplier'}
                            {payment.type === 'SUPPLIER_PREMIUM' && 'Premium Supplier'}
                            {payment.type === 'COMMUNITY_SUBSCRIPTION' && 'Community Subscription'}
                            {payment.type === 'COMMUNITY_ONE_TIME' && 'Community Access'}
                          </span>
                          <span className="acm-payment-from">
                            {payment.payerName}
                            {payment.communityName && <span className="acm-payment-community"> • {payment.communityName}</span>}
                          </span>
                        </div>
                        <div className="acm-payment-meta">
                          <span className="acm-payment-amount">{formatCurrency(payment.amount)}</span>
                          <span className="acm-payment-date">{formatDate(payment.createdAt)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Pending Merchant Payouts */}
              <div className="acm-payments-section">
                <h3>Pending Merchant Payouts</h3>
                {(paymentsData.pendingPayouts || []).length === 0 ? (
                  <div className="acm-empty-small">No pending payouts</div>
                ) : (
                  <div className="acm-payments-list">
                    {(paymentsData.pendingPayouts || []).map(payout => (
                      <div key={payout.id} className="acm-payout-row">
                        <div className="acm-payout-info">
                          <span className="acm-payout-merchant">{payout.merchantName}</span>
                          <span className="acm-payout-community">{payout.communityName}</span>
                        </div>
                        <div className="acm-payout-meta">
                          <span className="acm-payout-amount">{formatCurrency(payout.amount)}</span>
                          <button
                            className="acm-btn acm-btn-sm acm-btn-primary"
                            onClick={() => handleProcessPayout(payout.id)}
                          >
                            Process
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Revenue Breakdown */}
            <div className="acm-revenue-breakdown">
              <h3>Revenue Breakdown</h3>
              <div className="acm-breakdown-grid">
                <div className="acm-breakdown-item">
                  <span className="acm-breakdown-label">Supplier Verification Fees</span>
                  <span className="acm-breakdown-value">{formatCurrency(paymentsData.breakdown?.supplierVerification || 0)}</span>
                </div>
                <div className="acm-breakdown-item">
                  <span className="acm-breakdown-label">Featured Supplier Subscriptions</span>
                  <span className="acm-breakdown-value">{formatCurrency(paymentsData.breakdown?.supplierFeatured || 0)}</span>
                </div>
                <div className="acm-breakdown-item">
                  <span className="acm-breakdown-label">Premium Supplier Subscriptions</span>
                  <span className="acm-breakdown-value">{formatCurrency(paymentsData.breakdown?.supplierPremium || 0)}</span>
                </div>
                <div className="acm-breakdown-item">
                  <span className="acm-breakdown-label">Community Platform Fees</span>
                  <span className="acm-breakdown-value">{formatCurrency(paymentsData.breakdown?.communityFees || 0)}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const handleProcessPayout = async (payoutId) => {
    try {
      await api.post(`/admin/community/payouts/${payoutId}/process`);
      toast.success('Payout processed successfully');
      fetchPayments();
    } catch (err) {
      toast.error(err.message || 'Failed to process payout');
    }
  };

  /* =============================================
     PAYOUTS TAB
     ============================================= */

  const handleBatchProcessPayouts = async () => {
    if (selectedPayouts.length === 0) {
      toast.warning('Select payouts to process');
      return;
    }
    if (selectedPayouts.length > 20) {
      toast.warning('Maximum 20 payouts per batch');
      return;
    }
    setProcessingPayouts(true);
    try {
      const result = await api.post('/admin/community/payouts/batch-process', {
        payoutIds: selectedPayouts
      });
      toast.success(`Processed ${result.processed || 0} payouts`);
      setSelectedPayouts([]);
      fetchPayouts();
    } catch (err) {
      toast.error(err.message || 'Batch processing failed');
    } finally {
      setProcessingPayouts(false);
    }
  };

  const handleGenerateMonthlyPayouts = async () => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    const selectedPeriod = `${monthNames[payoutMonth]} ${payoutYear}`;
    if (!window.confirm(`Generate payouts for ${selectedPeriod}?`)) return;
    setProcessingPayouts(true);
    try {
      const result = await api.post('/admin/community/payouts/generate-monthly', {
        year: payoutYear,
        month: payoutMonth
      });
      toast.success(`Generated ${result.created?.length || 0} payouts for ${selectedPeriod}`);
      fetchPayouts();
    } catch (err) {
      toast.error(err.message || 'Failed to generate payouts');
    } finally {
      setProcessingPayouts(false);
    }
  };

  const handleGeneratePendingPayouts = async () => {
    if (!window.confirm('Generate payouts for ALL unpaid payments? This will create payout records for all community payments not yet included in a payout.')) return;
    setProcessingPayouts(true);
    try {
      const result = await api.post('/admin/community/payouts/generate-pending');
      const { created, skipped, minimumPayout } = result;
      if (created > 0) {
        toast.success(`Generated ${created} payouts`);
      } else if (skipped > 0) {
        toast.info(`No payouts generated. ${skipped} communities skipped (below ₦${minimumPayout} minimum)`);
      } else {
        toast.info('No pending payments to payout');
      }
      fetchPayouts();
    } catch (err) {
      toast.error(err.message || 'Failed to generate payouts');
    } finally {
      setProcessingPayouts(false);
    }
  };

  const togglePayoutSelection = (payoutId) => {
    setSelectedPayouts(prev =>
      prev.includes(payoutId)
        ? prev.filter(id => id !== payoutId)
        : [...prev, payoutId]
    );
  };

  const selectAllPendingPayouts = () => {
    const pendingIds = payouts.filter(p => p.status === 'PENDING').map(p => p.id);
    setSelectedPayouts(prev =>
      prev.length === pendingIds.length ? [] : pendingIds
    );
  };

  const renderPayouts = () => {
    const statusFilters = [
      { value: 'all', label: 'All' },
      { value: 'PENDING', label: 'Pending' },
      { value: 'COMPLETED', label: 'Completed' },
      { value: 'FAILED', label: 'Failed' }
    ];

    const getStatusBadge = (status) => {
      const styles = {
        PENDING: 'acm-badge-pending',
        COMPLETED: 'acm-badge-verified',
        FAILED: 'acm-badge-suspended',
        PROCESSING: 'acm-badge-processing'
      };
      return <span className={`acm-badge ${styles[status] || ''}`}>{status}</span>;
    };

    const pendingCount = payouts.filter(p => p.status === 'PENDING').length;

    return (
      <div className="acm-payouts">
        {/* Revenue Summary Cards */}
        {revenueSummary && (
          <div className="acm-stats-grid">
            <div className="acm-stat-card">
              <div className="acm-stat-icon acm-stat-icon-green">
                <Icons.CheckCircle size={24} />
              </div>
              <div className="acm-stat-content">
                <span className="acm-stat-value">{formatCurrency(revenueSummary.totalPlatformRevenue || 0)}</span>
                <span className="acm-stat-label">Platform Revenue</span>
              </div>
            </div>
            <div className="acm-stat-card">
              <div className="acm-stat-icon acm-stat-icon-blue">
                <Icons.TrendingUp size={24} />
              </div>
              <div className="acm-stat-content">
                <span className="acm-stat-value">{formatCurrency(revenueSummary.totalMerchantPayouts || 0)}</span>
                <span className="acm-stat-label">Merchant Payouts</span>
              </div>
            </div>
            <div className="acm-stat-card">
              <div className="acm-stat-icon acm-stat-icon-orange">
                <Icons.AlertTriangle size={24} />
              </div>
              <div className="acm-stat-content">
                <span className="acm-stat-value">{formatCurrency(revenueSummary.pendingPayoutAmount || 0)}</span>
                <span className="acm-stat-label">Pending Payouts</span>
              </div>
            </div>
            <div className="acm-stat-card">
              <div className="acm-stat-icon acm-stat-icon-purple">
                <Icons.Users size={24} />
              </div>
              <div className="acm-stat-content">
                <span className="acm-stat-value">{revenueSummary.pendingPayoutCount || 0}</span>
                <span className="acm-stat-label">Awaiting Payment</span>
              </div>
            </div>
          </div>
        )}

        {/* Actions Bar */}
        <div className="acm-payouts-actions">
          <div className="acm-filters">
            {statusFilters.map(f => (
              <button
                key={f.value}
                className={`acm-filter-btn ${payoutsStatus === f.value ? 'active' : ''}`}
                onClick={() => { setPayoutsStatus(f.value); setPayoutsPage(1); }}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="acm-payouts-btns">
            <div className="acm-payout-month-selector">
              <select
                className="acm-select"
                value={payoutMonth}
                onChange={(e) => setPayoutMonth(parseInt(e.target.value))}
              >
                <option value={0}>January</option>
                <option value={1}>February</option>
                <option value={2}>March</option>
                <option value={3}>April</option>
                <option value={4}>May</option>
                <option value={5}>June</option>
                <option value={6}>July</option>
                <option value={7}>August</option>
                <option value={8}>September</option>
                <option value={9}>October</option>
                <option value={10}>November</option>
                <option value={11}>December</option>
              </select>
              <select
                className="acm-select"
                value={payoutYear}
                onChange={(e) => setPayoutYear(parseInt(e.target.value))}
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <button
              className="acm-btn acm-btn-secondary"
              onClick={handleGenerateMonthlyPayouts}
              disabled={processingPayouts}
              title="Generate payouts for selected month only"
            >
              <Icons.RefreshCw size={16} />
              For Month
            </button>
            <button
              className="acm-btn acm-btn-primary"
              onClick={handleGeneratePendingPayouts}
              disabled={processingPayouts}
              title="Generate payouts for ALL unpaid payments"
            >
              <Icons.RefreshCw size={16} />
              All Pending
            </button>
            {selectedPayouts.length > 0 && (
              <button
                className="acm-btn acm-btn-primary"
                onClick={handleBatchProcessPayouts}
                disabled={processingPayouts}
              >
                {processingPayouts ? 'Processing...' : `Process ${selectedPayouts.length} Selected`}
              </button>
            )}
          </div>
        </div>

        {/* Payouts Table */}
        <div className="acm-table-container">
          <table className="acm-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  {pendingCount > 0 && (
                    <input
                      type="checkbox"
                      checked={selectedPayouts.length === pendingCount && pendingCount > 0}
                      onChange={selectAllPendingPayouts}
                    />
                  )}
                </th>
                <th>Merchant</th>
                <th>Community</th>
                <th>Amount</th>
                <th>Platform Fee</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payouts.length === 0 ? (
                <tr>
                  <td colSpan={8} className="acm-empty-row">No payouts found</td>
                </tr>
              ) : (
                payouts.map(payout => (
                  <tr key={payout.id}>
                    <td>
                      {payout.status === 'PENDING' && (
                        <input
                          type="checkbox"
                          checked={selectedPayouts.includes(payout.id)}
                          onChange={() => togglePayoutSelection(payout.id)}
                        />
                      )}
                    </td>
                    <td>
                      <div className="acm-cell-stack">
                        <span className="acm-cell-main">{payout.merchant?.firstName} {payout.merchant?.lastName}</span>
                        <span className="acm-cell-sub">{payout.merchant?.email}</span>
                      </div>
                    </td>
                    <td>{payout.community?.name || '-'}</td>
                    <td className="acm-cell-amount">{formatCurrency(payout.amount)}</td>
                    <td className="acm-cell-fee">{formatCurrency(payout.platformFee || 0)}</td>
                    <td>{getStatusBadge(payout.status)}</td>
                    <td>{formatDate(payout.createdAt)}</td>
                    <td>
                      {payout.status === 'PENDING' && (
                        <button
                          className="acm-btn acm-btn-sm acm-btn-primary"
                          onClick={() => handleProcessPayout(payout.id)}
                        >
                          Process
                        </button>
                      )}
                      {payout.status === 'COMPLETED' && (
                        <span className="acm-text-muted">{payout.transferReference || 'Paid'}</span>
                      )}
                      {payout.status === 'FAILED' && (
                        <span className="acm-text-danger" title={payout.failureReason}>Failed</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {payoutsTotal > 20 && (
          <div className="acm-pagination">
            <button
              disabled={payoutsPage === 1}
              onClick={() => setPayoutsPage(p => p - 1)}
            >
              <Icons.ChevronLeft size={18} />
            </button>
            <span>Page {payoutsPage} of {Math.ceil(payoutsTotal / 20)}</span>
            <button
              disabled={payoutsPage >= Math.ceil(payoutsTotal / 20)}
              onClick={() => setPayoutsPage(p => p + 1)}
            >
              <Icons.ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderSettings = () => {
    // Show loading if settings are not ready
    if (!settings) {
      return <div className="acm-loading">Loading settings...</div>;
    }

    // Show error if tiers failed to load
    if (availableTiers.length === 0) {
      return (
        <div className="acm-empty">
          <Icons.AlertTriangle size={48} />
          <p>Failed to load tiers. Please refresh the page.</p>
        </div>
      );
    }

    // Use dynamically fetched tiers from database
    const tiers = availableTiers;

    const renderTierCheckboxes = (field) => (
      <div className="acm-tier-checkboxes">
        {tiers.map(tier => (
          <label key={tier} className="acm-tier-checkbox">
            <input
              type="checkbox"
              checked={(settings[field] || []).includes(tier)}
              onChange={(e) => {
                const current = settings[field] || [];
                const updated = e.target.checked
                  ? [...current, tier]
                  : current.filter(t => t !== tier);
                setSettings({ ...settings, [field]: updated });
              }}
            />
            <span className={`acm-tier-label acm-tier-${tier.toLowerCase()}`}>{tier}</span>
          </label>
        ))}
      </div>
    );

    const settingsTabs = [
      { id: 'features', label: 'Features' },
      { id: 'tiers', label: 'Tier Permissions' },
      { id: 'suppliers', label: 'Suppliers' },
      { id: 'paid', label: 'Paid Communities' },
      { id: 'reviews', label: 'Reviews' },
      { id: 'moderation', label: 'Moderation' },
      { id: 'gamification', label: 'Gamification' }
    ];

    return (
      <div className="acm-section">
        <div className="acm-section-header">
          <h2>Community Settings</h2>
          <button
            className="acm-btn acm-btn-primary"
            onClick={handleSaveSettings}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="acm-btn-spinner" />
                Saving...
              </>
            ) : (
              <>
                <Icons.CheckCircle size={18} />
                Save Changes
              </>
            )}
          </button>
        </div>

        {/* Settings Sub-Tabs */}
        <div className="acm-settings-tabs">
          {settingsTabs.map(tab => (
            <button
              key={tab.id}
              className={`acm-settings-tab ${settingsTab === tab.id ? 'active' : ''}`}
              onClick={() => setSettingsTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Features Tab */}
        {settingsTab === 'features' && (
          <div className="acm-settings-content">
            <div className="acm-settings-intro">
              <h3>Feature Toggles</h3>
              <p>Enable or disable major platform features across the entire community hub.</p>
            </div>

            <div className="acm-settings-info" style={{ marginBottom: '1.5rem', marginTop: 0 }}>
              <Icons.Info size={20} />
              <div>
                <strong>Community Types Explained:</strong>
                <p style={{ marginTop: '0.5rem' }}>
                  <strong>Mastermind</strong> = Platform-managed groups where <em>merchants</em> connect with each other for peer learning and networking.<br />
                  <strong>Brand Community</strong> = Merchant-managed VIP clubs where merchants build communities with their <em>customers</em>.
                </p>
              </div>
            </div>

            <div className="acm-settings-grid">
              <div className="acm-feature-card">
                <div className="acm-feature-header">
                  <div className="acm-feature-icon acm-feature-icon-blue">
                    <Icons.Users size={24} />
                  </div>
                  <div className="acm-feature-info">
                    <h4>Mastermind Communities</h4>
                    <p>Platform-managed peer learning groups for <strong>merchants only</strong>. Organized by niche (Fashion, Food), city (Lagos, Abuja), or business stage (Beginner, Growth).</p>
                  </div>
                  <label className="acm-toggle">
                    <input
                      type="checkbox"
                      checked={settings.mastermindEnabled}
                      onChange={(e) => setSettings({ ...settings, mastermindEnabled: e.target.checked })}
                    />
                    <span className="acm-toggle-slider"></span>
                  </label>
                </div>
              </div>

              <div className="acm-feature-card">
                <div className="acm-feature-header">
                  <div className="acm-feature-icon acm-feature-icon-purple">
                    <Icons.Star size={24} />
                  </div>
                  <div className="acm-feature-info">
                    <h4>Brand Communities</h4>
                    <p>Merchant-managed VIP clubs for their <strong>customers</strong>. Each merchant can create a community for their store's loyal customers.</p>
                  </div>
                  <label className="acm-toggle">
                    <input
                      type="checkbox"
                      checked={settings.brandEnabled}
                      onChange={(e) => setSettings({ ...settings, brandEnabled: e.target.checked })}
                    />
                    <span className="acm-toggle-slider"></span>
                  </label>
                </div>
              </div>

              <div className="acm-feature-card">
                <div className="acm-feature-header">
                  <div className="acm-feature-icon acm-feature-icon-green">
                    <Icons.Package size={24} />
                  </div>
                  <div className="acm-feature-info">
                    <h4>Supplier Directory</h4>
                    <p>Verified suppliers marketplace for merchants to source products</p>
                  </div>
                  <label className="acm-toggle">
                    <input
                      type="checkbox"
                      checked={settings.suppliersEnabled}
                      onChange={(e) => setSettings({ ...settings, suppliersEnabled: e.target.checked })}
                    />
                    <span className="acm-toggle-slider"></span>
                  </label>
                </div>
              </div>

              <div className="acm-feature-card">
                <div className="acm-feature-header">
                  <div className="acm-feature-icon acm-feature-icon-orange">
                    <Icons.BarChart size={24} />
                  </div>
                  <div className="acm-feature-info">
                    <h4>Benchmarks</h4>
                    <p>Anonymized performance comparison between similar merchants</p>
                  </div>
                  <label className="acm-toggle">
                    <input
                      type="checkbox"
                      checked={settings.benchmarksEnabled}
                      onChange={(e) => setSettings({ ...settings, benchmarksEnabled: e.target.checked })}
                    />
                    <span className="acm-toggle-slider"></span>
                  </label>
                </div>
              </div>

              <div className="acm-feature-card">
                <div className="acm-feature-header">
                  <div className="acm-feature-icon acm-feature-icon-yellow">
                    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                      <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/>
                    </svg>
                  </div>
                  <div className="acm-feature-info">
                    <h4>Paid Communities</h4>
                    <p>Allow merchants to charge monthly subscription for their communities</p>
                  </div>
                  <label className="acm-toggle">
                    <input
                      type="checkbox"
                      checked={settings.paidCommunitiesEnabled}
                      onChange={(e) => setSettings({ ...settings, paidCommunitiesEnabled: e.target.checked })}
                    />
                    <span className="acm-toggle-slider"></span>
                  </label>
                </div>
              </div>

              <div className="acm-feature-card">
                <div className="acm-feature-header">
                  <div className="acm-feature-icon acm-feature-icon-teal">
                    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M16 8l-4 4-4-4"/>
                      <path d="M8 12l4 4 4-4"/>
                    </svg>
                  </div>
                  <div className="acm-feature-info">
                    <h4>Group Deals</h4>
                    <p>Supplier bulk purchasing deals for merchants to get discounts</p>
                  </div>
                  <label className="acm-toggle">
                    <input
                      type="checkbox"
                      checked={settings.groupDealsEnabled}
                      onChange={(e) => setSettings({ ...settings, groupDealsEnabled: e.target.checked })}
                    />
                    <span className="acm-toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tier Permissions Tab */}
        {settingsTab === 'tiers' && (
          <div className="acm-settings-content">
            <div className="acm-settings-intro">
              <h3>Tier Permissions</h3>
              <p>Control which merchant tiers can access each feature. Check tiers to grant access.</p>
            </div>

            <div className="acm-tier-grid">
              <div className="acm-tier-card">
                <h4>Mastermind Access</h4>
                <p>Which merchant tiers can join mastermind communities (peer learning for merchants)</p>
                {renderTierCheckboxes('mastermindTiers')}
              </div>

              <div className="acm-tier-card">
                <h4>Brand Community Access</h4>
                <p>Which merchant tiers can create brand communities for their customers (VIP clubs)</p>
                {renderTierCheckboxes('brandTiers')}
              </div>

              <div className="acm-tier-card">
                <h4>Supplier Directory Access</h4>
                <p>Which tiers can view and contact suppliers</p>
                {renderTierCheckboxes('supplierTiers')}
              </div>

              <div className="acm-tier-card">
                <h4>Community Creation</h4>
                <p>Which tiers can create new communities</p>
                {renderTierCheckboxes('communityCreationTiers')}
              </div>

              <div className="acm-tier-card">
                <h4>Paid Community Creation</h4>
                <p>Which tiers can create paid/subscription communities</p>
                {renderTierCheckboxes('paidCommunityTiers')}
              </div>
            </div>

            {/* Community Limits per Tier */}
            <div className="acm-settings-section" style={{ marginTop: '2rem' }}>
              <h3 style={{ marginBottom: '0.5rem' }}>Community Limits per Tier</h3>
              <p style={{ color: '#6b7280', marginBottom: '1rem' }}>Set the maximum number of brand communities each merchant tier can create.</p>

              <div className="acm-tier-limits-grid">
                {availableTiers.map(tier => (
                  <div key={tier} className="acm-tier-limit-card">
                    <div className="acm-tier-limit-header">
                      <span className="acm-tier-limit-name">{tier}</span>
                    </div>
                    <div className="acm-tier-limit-input">
                      <label>Max Communities</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={settings?.tierCommunityLimits?.[tier] ?? (tier === 'FREE' ? 1 : tier === 'PRO' ? 3 : tier === 'PREMIUM' ? 10 : 999)}
                        onChange={(e) => setSettings({
                          ...settings,
                          tierCommunityLimits: {
                            ...settings?.tierCommunityLimits,
                            [tier]: parseInt(e.target.value) || 0
                          }
                        })}
                        placeholder="0 = unlimited"
                      />
                      <span className="acm-tier-limit-hint">0 = unlimited</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Suppliers Tab */}
        {settingsTab === 'suppliers' && (
          <div className="acm-settings-content">
            <div className="acm-settings-intro">
              <h3>Supplier Settings</h3>
              <p>Configure supplier registration, verification, and pricing.</p>
            </div>

            <div className="acm-settings-grid acm-settings-grid-2">
              {/* Verification Settings */}
              <div className="acm-settings-card">
                <h4>Verification</h4>
                <div className="acm-setting-row">
                  <label className="acm-setting-toggle">
                    <span>Require Verification</span>
                    <label className="acm-toggle acm-toggle-sm">
                      <input
                        type="checkbox"
                        checked={settings.supplierVerificationRequired}
                        onChange={(e) => setSettings({ ...settings, supplierVerificationRequired: e.target.checked })}
                      />
                      <span className="acm-toggle-slider"></span>
                    </label>
                  </label>
                  <span className="acm-setting-hint">Suppliers must be verified before appearing in directory</span>
                </div>
                <div className="acm-setting-row">
                  <label className="acm-setting-toggle">
                    <span>Auto-Approve</span>
                    <label className="acm-toggle acm-toggle-sm">
                      <input
                        type="checkbox"
                        checked={settings.supplierAutoApprove}
                        onChange={(e) => setSettings({ ...settings, supplierAutoApprove: e.target.checked })}
                      />
                      <span className="acm-toggle-slider"></span>
                    </label>
                  </label>
                  <span className="acm-setting-hint">Automatically approve suppliers (not recommended)</span>
                </div>
                <div className="acm-setting-row">
                  <label>Minimum Products Required</label>
                  <input
                    type="number"
                    min="0"
                    value={settings.supplierMinProducts || 0}
                    onChange={(e) => setSettings({ ...settings, supplierMinProducts: parseInt(e.target.value) || 0 })}
                  />
                  <span className="acm-setting-hint">Minimum products a supplier must add</span>
                </div>
              </div>

              {/* Product Limits */}
              <div className="acm-settings-card">
                <h4>Product Limits by Tier</h4>
                <div className="acm-setting-row">
                  <label>Basic Tier</label>
                  <input
                    type="number"
                    min="1"
                    value={settings.supplierMaxProductsBasic || 10}
                    onChange={(e) => setSettings({ ...settings, supplierMaxProductsBasic: parseInt(e.target.value) || 10 })}
                  />
                </div>
                <div className="acm-setting-row">
                  <label>Featured Tier</label>
                  <input
                    type="number"
                    min="1"
                    value={settings.supplierMaxProductsFeatured || 50}
                    onChange={(e) => setSettings({ ...settings, supplierMaxProductsFeatured: parseInt(e.target.value) || 50 })}
                  />
                </div>
                <div className="acm-setting-row">
                  <label>Premium Tier</label>
                  <input
                    type="number"
                    min="1"
                    value={settings.supplierMaxProductsPremium || 500}
                    onChange={(e) => setSettings({ ...settings, supplierMaxProductsPremium: parseInt(e.target.value) || 500 })}
                  />
                </div>
              </div>

              {/* Pricing */}
              <div className="acm-settings-card">
                <h4>Supplier Pricing (NGN)</h4>
                <div className="acm-setting-row">
                  <label>Verification Fee (One-time)</label>
                  <div className="acm-input-with-prefix">
                    <span>₦</span>
                    <input
                      type="number"
                      min="0"
                      value={settings.supplierVerificationFee || 0}
                      onChange={(e) => setSettings({ ...settings, supplierVerificationFee: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                <div className="acm-setting-row">
                  <label>Featured Tier (Monthly)</label>
                  <div className="acm-input-with-prefix">
                    <span>₦</span>
                    <input
                      type="number"
                      min="0"
                      value={settings.supplierFeaturedFee || 0}
                      onChange={(e) => setSettings({ ...settings, supplierFeaturedFee: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                <div className="acm-setting-row">
                  <label>Premium Tier (Monthly)</label>
                  <div className="acm-input-with-prefix">
                    <span>₦</span>
                    <input
                      type="number"
                      min="0"
                      value={settings.supplierPremiumFee || 0}
                      onChange={(e) => setSettings({ ...settings, supplierPremiumFee: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                <div className="acm-setting-row">
                  <label>Group Deal Commission (%)</label>
                  <div className="acm-input-with-suffix">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={settings.groupDealFeePercent || 0}
                      onChange={(e) => setSettings({ ...settings, groupDealFeePercent: parseFloat(e.target.value) || 0 })}
                    />
                    <span>%</span>
                  </div>
                </div>
              </div>

              {/* Group Deal Tier Access */}
              <div className="acm-settings-card">
                <h4>Group Deal Access Control</h4>
                <div className="acm-setting-row">
                  <label>Supplier Tiers (Create Deals)</label>
                  <div className="acm-tier-checkboxes">
                    {['BASIC', 'FEATURED', 'PREMIUM'].map(tier => (
                      <label key={tier} className="acm-tier-checkbox">
                        <input
                          type="checkbox"
                          checked={(settings.groupDealSupplierTiers || ['FEATURED', 'PREMIUM']).includes(tier)}
                          onChange={(e) => {
                            const current = settings.groupDealSupplierTiers || ['FEATURED', 'PREMIUM'];
                            const updated = e.target.checked
                              ? [...current, tier]
                              : current.filter(t => t !== tier);
                            setSettings({ ...settings, groupDealSupplierTiers: updated });
                          }}
                        />
                        <span className={`acm-tier-label acm-tier-${tier.toLowerCase()}`}>{tier}</span>
                      </label>
                    ))}
                  </div>
                  <span className="acm-setting-hint">Which supplier tiers can create group deals</span>
                </div>
                <div className="acm-setting-row">
                  <label>Merchant Tiers (View/Join Deals)</label>
                  <div className="acm-tier-checkboxes">
                    {availableTiers.map(tier => (
                      <label key={tier} className="acm-tier-checkbox">
                        <input
                          type="checkbox"
                          checked={(settings.groupDealMerchantTiers || ['PRO', 'PREMIUM']).includes(tier)}
                          onChange={(e) => {
                            const current = settings.groupDealMerchantTiers || ['PRO', 'PREMIUM'];
                            const updated = e.target.checked
                              ? [...current, tier]
                              : current.filter(t => t !== tier);
                            setSettings({ ...settings, groupDealMerchantTiers: updated });
                          }}
                        />
                        <span className={`acm-tier-label acm-tier-${tier.toLowerCase().replace(' ', '-')}`}>{tier}</span>
                      </label>
                    ))}
                  </div>
                  <span className="acm-setting-hint">Which merchant tiers can browse and join group deals</span>
                </div>
              </div>

              {/* Group Deal Limits per Supplier Tier */}
              <div className="acm-settings-card">
                <h4>Deal Limits per Supplier Tier</h4>
                <p className="acm-card-description">Maximum number of active deals each supplier tier can have at once</p>
                <div className="acm-setting-row">
                  <label>BASIC Tier Limit</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={settings.groupDealLimitBasic ?? 0}
                    onChange={(e) => setSettings({ ...settings, groupDealLimitBasic: parseInt(e.target.value) || 0 })}
                  />
                  <span className="acm-setting-hint">Set to 0 to prevent BASIC tier from creating deals</span>
                </div>
                <div className="acm-setting-row">
                  <label>FEATURED Tier Limit</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={settings.groupDealLimitFeatured ?? 3}
                    onChange={(e) => setSettings({ ...settings, groupDealLimitFeatured: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="acm-setting-row">
                  <label>PREMIUM Tier Limit</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={settings.groupDealLimitPremium ?? 10}
                    onChange={(e) => setSettings({ ...settings, groupDealLimitPremium: parseInt(e.target.value) || 0 })}
                  />
                  <span className="acm-setting-hint">Premium suppliers should have the highest limit</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Paid Communities Tab */}
        {settingsTab === 'paid' && (
          <div className="acm-settings-content">
            <div className="acm-settings-intro">
              <h3>Paid Community Settings</h3>
              <p>Configure pricing and fees for merchant-created paid communities.</p>
            </div>

            <div className="acm-settings-grid acm-settings-grid-2">
              <div className="acm-settings-card">
                <h4>Platform Fees</h4>
                <div className="acm-setting-row">
                  <label>Platform Commission</label>
                  <div className="acm-input-with-suffix">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={settings.platformCommunityFeePercent || 0}
                      onChange={(e) => setSettings({ ...settings, platformCommunityFeePercent: parseFloat(e.target.value) || 0 })}
                    />
                    <span>%</span>
                  </div>
                  <span className="acm-setting-hint">Percentage taken from paid community subscriptions</span>
                </div>
              </div>

              <div className="acm-settings-card">
                <h4>Price Limits (NGN)</h4>
                <div className="acm-setting-row">
                  <label>Minimum Price</label>
                  <div className="acm-input-with-prefix">
                    <span>₦</span>
                    <input
                      type="number"
                      min="0"
                      value={settings.minCommunityPrice || 0}
                      onChange={(e) => setSettings({ ...settings, minCommunityPrice: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <span className="acm-setting-hint">Minimum monthly subscription price</span>
                </div>
                <div className="acm-setting-row">
                  <label>Maximum Price</label>
                  <div className="acm-input-with-prefix">
                    <span>₦</span>
                    <input
                      type="number"
                      min="0"
                      value={settings.maxCommunityPrice || 0}
                      onChange={(e) => setSettings({ ...settings, maxCommunityPrice: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <span className="acm-setting-hint">Maximum monthly subscription price</span>
                </div>
              </div>

              <div className="acm-settings-card">
                <h4>Payout Settings</h4>
                <div className="acm-setting-row">
                  <label>Minimum Payout Amount</label>
                  <div className="acm-input-with-prefix">
                    <span>₦</span>
                    <input
                      type="number"
                      min="0"
                      value={settings.minimumPayout || 5000}
                      onChange={(e) => setSettings({ ...settings, minimumPayout: parseFloat(e.target.value) || 5000 })}
                    />
                  </div>
                  <span className="acm-setting-hint">Minimum balance required before merchants can request payout</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reviews Tab */}
        {settingsTab === 'reviews' && (
          <div className="acm-settings-content">
            <div className="acm-settings-intro">
              <h3>Review Settings</h3>
              <p>Configure supplier review system behavior.</p>
            </div>

            <div className="acm-settings-grid acm-settings-grid-2">
              <div className="acm-settings-card">
                <h4>Review System</h4>
                <div className="acm-setting-row">
                  <label className="acm-setting-toggle">
                    <span>Enable Reviews</span>
                    <label className="acm-toggle acm-toggle-sm">
                      <input
                        type="checkbox"
                        checked={settings.reviewsEnabled}
                        onChange={(e) => setSettings({ ...settings, reviewsEnabled: e.target.checked })}
                      />
                      <span className="acm-toggle-slider"></span>
                    </label>
                  </label>
                </div>
                <div className="acm-setting-row">
                  <label className="acm-setting-toggle">
                    <span>Require Verified Purchase</span>
                    <label className="acm-toggle acm-toggle-sm">
                      <input
                        type="checkbox"
                        checked={settings.requireVerifiedPurchase}
                        onChange={(e) => setSettings({ ...settings, requireVerifiedPurchase: e.target.checked })}
                      />
                      <span className="acm-toggle-slider"></span>
                    </label>
                  </label>
                  <span className="acm-setting-hint">Only merchants who purchased can review</span>
                </div>
              </div>

              <div className="acm-settings-card">
                <h4>Rating Range</h4>
                <div className="acm-setting-row">
                  <label>Minimum Rating</label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    value={settings.reviewMinRating || 1}
                    onChange={(e) => setSettings({ ...settings, reviewMinRating: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div className="acm-setting-row">
                  <label>Maximum Rating</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={settings.reviewMaxRating || 5}
                    onChange={(e) => setSettings({ ...settings, reviewMaxRating: parseInt(e.target.value) || 5 })}
                  />
                </div>
                <div className="acm-setting-row">
                  <label>Minimum Review Length</label>
                  <input
                    type="number"
                    min="0"
                    value={settings.reviewMinLength || 0}
                    onChange={(e) => setSettings({ ...settings, reviewMinLength: parseInt(e.target.value) || 0 })}
                  />
                  <span className="acm-setting-hint">Minimum characters for review text</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Moderation Tab */}
        {settingsTab === 'moderation' && (
          <div className="acm-settings-content">
            <div className="acm-settings-intro">
              <h3>Moderation Settings</h3>
              <p>Configure auto-moderation, rate limits, and content filtering.</p>
            </div>

            <div className="acm-settings-grid acm-settings-grid-2">
              <div className="acm-settings-card">
                <h4>Rate Limits</h4>
                <div className="acm-setting-row">
                  <label>Max Posts Per Hour</label>
                  <input
                    type="number"
                    min="1"
                    value={settings.maxPostsPerHour || 5}
                    onChange={(e) => setSettings({ ...settings, maxPostsPerHour: parseInt(e.target.value) || 5 })}
                  />
                </div>
                <div className="acm-setting-row">
                  <label>Max Comments Per Hour</label>
                  <input
                    type="number"
                    min="1"
                    value={settings.maxCommentsPerHour || 20}
                    onChange={(e) => setSettings({ ...settings, maxCommentsPerHour: parseInt(e.target.value) || 20 })}
                  />
                </div>
                <div className="acm-setting-row">
                  <label>New Member Cooldown (Hours)</label>
                  <input
                    type="number"
                    min="0"
                    value={settings.newMemberCooldownHours || 24}
                    onChange={(e) => setSettings({ ...settings, newMemberCooldownHours: parseInt(e.target.value) || 24 })}
                  />
                  <span className="acm-setting-hint">Hours before new members can post</span>
                </div>
                <div className="acm-setting-row">
                  <label>Max Links Per Post</label>
                  <input
                    type="number"
                    min="0"
                    value={settings.maxLinksPerPost || 3}
                    onChange={(e) => setSettings({ ...settings, maxLinksPerPost: parseInt(e.target.value) || 3 })}
                  />
                </div>
              </div>

              <div className="acm-settings-card">
                <h4>Auto-Moderation</h4>
                <div className="acm-setting-row">
                  <label className="acm-setting-toggle">
                    <span>Profanity Filter</span>
                    <label className="acm-toggle acm-toggle-sm">
                      <input
                        type="checkbox"
                        checked={settings.profanityFilterEnabled}
                        onChange={(e) => setSettings({ ...settings, profanityFilterEnabled: e.target.checked })}
                      />
                      <span className="acm-toggle-slider"></span>
                    </label>
                  </label>
                </div>
                <div className="acm-setting-row">
                  <label className="acm-setting-toggle">
                    <span>Spam Detection</span>
                    <label className="acm-toggle acm-toggle-sm">
                      <input
                        type="checkbox"
                        checked={settings.spamDetectionEnabled}
                        onChange={(e) => setSettings({ ...settings, spamDetectionEnabled: e.target.checked })}
                      />
                      <span className="acm-toggle-slider"></span>
                    </label>
                  </label>
                </div>
                <div className="acm-setting-row">
                  <label className="acm-setting-toggle">
                    <span>Require Approval for New Users</span>
                    <label className="acm-toggle acm-toggle-sm">
                      <input
                        type="checkbox"
                        checked={settings.requireApprovalNewUsers}
                        onChange={(e) => setSettings({ ...settings, requireApprovalNewUsers: e.target.checked })}
                      />
                      <span className="acm-toggle-slider"></span>
                    </label>
                  </label>
                  <span className="acm-setting-hint">New user posts require moderator approval</span>
                </div>
              </div>

              <div className="acm-settings-card acm-settings-card-full">
                <h4>Profanity Word List</h4>
                <div className="acm-setting-row">
                  <textarea
                    placeholder="Enter words separated by commas (e.g., word1, word2, word3)..."
                    value={profanityWordListText}
                    onChange={(e) => setProfanityWordListText(e.target.value)}
                    onBlur={(e) => {
                      const words = e.target.value
                        .split(',')
                        .map(w => w.trim())
                        .filter(Boolean);
                      setSettings({ ...settings, profanityWordList: words });
                    }}
                    rows={4}
                  />
                  <span className="acm-setting-hint">
                    {Array.isArray(settings.profanityWordList)
                      ? `${settings.profanityWordList.length} words configured`
                      : 'Words will be filtered from posts and comments'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Gamification Tab */}
        {settingsTab === 'gamification' && (
          <div className="acm-settings-content">
            <div className="acm-settings-intro">
              <h3>Gamification Settings</h3>
              <p>Configure points, levels, and rewards for community engagement.</p>
            </div>

            <div className="acm-settings-grid acm-settings-grid-2">
              <div className="acm-settings-card">
                <h4>Enable Gamification</h4>
                <div className="acm-setting-row">
                  <label className="acm-setting-toggle">
                    <span>Enable Points System</span>
                    <label className="acm-toggle acm-toggle-sm">
                      <input
                        type="checkbox"
                        checked={settings.gamificationEnabled}
                        onChange={(e) => setSettings({ ...settings, gamificationEnabled: e.target.checked })}
                      />
                      <span className="acm-toggle-slider"></span>
                    </label>
                  </label>
                  <span className="acm-setting-hint">Members earn points for engagement</span>
                </div>
              </div>

              <div className="acm-settings-card">
                <h4>Points Configuration</h4>
                <div className="acm-points-grid">
                  <div className="acm-point-item">
                    <label>Per Post</label>
                    <input
                      type="number"
                      min="0"
                      value={settings.pointsPerPost || 0}
                      onChange={(e) => setSettings({ ...settings, pointsPerPost: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="acm-point-item">
                    <label>Per Comment</label>
                    <input
                      type="number"
                      min="0"
                      value={settings.pointsPerComment || 0}
                      onChange={(e) => setSettings({ ...settings, pointsPerComment: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="acm-point-item">
                    <label>Per Like Received</label>
                    <input
                      type="number"
                      min="0"
                      value={settings.pointsPerLikeReceived || 0}
                      onChange={(e) => setSettings({ ...settings, pointsPerLikeReceived: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="acm-point-item">
                    <label>Per Helpful Vote</label>
                    <input
                      type="number"
                      min="0"
                      value={settings.pointsPerHelpful || 0}
                      onChange={(e) => setSettings({ ...settings, pointsPerHelpful: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="acm-point-item">
                    <label>Per Review</label>
                    <input
                      type="number"
                      min="0"
                      value={settings.pointsPerReview || 0}
                      onChange={(e) => setSettings({ ...settings, pointsPerReview: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="acm-settings-info">
              <Icons.TrendingUp size={20} />
              <div>
                <strong>Level System</strong>
                <p>Members level up every 100 points. Level = (Total Points / 100) + 1</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTierModal = () => {
    if (!tierModalData) return null;

    const tierColors = {
      BRONZE: { bg: '#fef3c7', color: '#92400e' },
      SILVER: { bg: '#f3f4f6', color: '#4b5563' },
      GOLD: { bg: '#fef3c7', color: '#b45309' },
      PLATINUM: { bg: '#e0e7ff', color: '#4338ca' }
    };

    return (
      <div className="acm-modal-overlay" onClick={handleCloseTierModal}>
        <div className="acm-modal acm-modal-lg" onClick={e => e.stopPropagation()}>
          <div className="acm-modal-header">
            <h3>Customer Tiers - {tierModalData.name}</h3>
            <button onClick={handleCloseTierModal}><Icons.X size={20} /></button>
          </div>
          <div className="acm-modal-body">
            {tierLoading ? (
              <div className="acm-loading">Loading tier stats...</div>
            ) : tierStats ? (
              <>
                <div className="acm-tier-stats-grid">
                  {['PLATINUM', 'GOLD', 'SILVER', 'BRONZE'].map(tier => (
                    <div key={tier} className="acm-tier-stat-card" style={{ borderColor: tierColors[tier].bg }}>
                      <div className="acm-tier-stat-icon" style={{ background: tierColors[tier].bg, color: tierColors[tier].color }}>
                        <Icons.Star size={20} />
                      </div>
                      <div className="acm-tier-stat-content">
                        <span className="acm-tier-stat-value">{tierStats.tiers?.[tier] || 0}</span>
                        <span className="acm-tier-stat-label">{tier}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="acm-tier-total">
                  Total Members: <strong>{tierStats.total || 0}</strong>
                </div>
                <div className="acm-tier-info">
                  <p><strong>Tier Thresholds:</strong></p>
                  <ul>
                    <li>Bronze: 0-499 points</li>
                    <li>Silver: 500-1,999 points</li>
                    <li>Gold: 2,000-4,999 points</li>
                    <li>Platinum: 5,000+ points</li>
                  </ul>
                  <p className="acm-text-muted">Points: 1 per ₦100 spent + 50 per completed order</p>
                </div>
              </>
            ) : (
              <div className="acm-empty">No tier data available</div>
            )}
          </div>
          <div className="acm-modal-footer">
            <button className="acm-btn acm-btn-secondary" onClick={handleCloseTierModal}>Close</button>
            <button
              className="acm-btn acm-btn-primary"
              onClick={handleRecalculateTiers}
              disabled={tierLoading}
            >
              <Icons.RefreshCw size={16} />
              Recalculate Tiers
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderModal = () => {
    if (!showModal) return null;

    if (modalType === 'create-community') {
      return (
        <div className="acm-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="acm-modal" onClick={e => e.stopPropagation()}>
            <div className="acm-modal-header">
              <h3>Create Community</h3>
              <button onClick={() => setShowModal(false)}><Icons.X size={20} /></button>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              handleCreateCommunity({
                name: formData.get('name'),
                description: formData.get('description'),
                type: formData.get('type'),
                category: formData.get('category'),
                city: formData.get('city'),
                isPublic: formData.get('isPublic') === 'on'
              });
            }}>
              <div className="acm-form-group">
                <label>Name *</label>
                <input name="name" required placeholder="Community name" />
              </div>
              <div className="acm-form-group">
                <label>Description</label>
                <textarea name="description" rows={3} placeholder="Describe this community..." />
              </div>
              <div className="acm-form-row">
                <div className="acm-form-group">
                  <label>Type</label>
                  <select name="type">
                    <option value="MASTERMIND_NICHE">Niche Mastermind</option>
                    <option value="MASTERMIND_CITY">City Mastermind</option>
                    <option value="MASTERMIND_STAGE">Stage Mastermind</option>
                  </select>
                </div>
                <div className="acm-form-group">
                  <label>Category</label>
                  <input name="category" placeholder="e.g., Fashion, Food" />
                </div>
              </div>
              <div className="acm-form-group">
                <label>City (for City Masterminds)</label>
                <input name="city" placeholder="e.g., Lagos, Abuja" />
              </div>
              <div className="acm-form-group">
                <label>
                  <input type="checkbox" name="isPublic" defaultChecked /> Public Community
                </label>
              </div>
              <div className="acm-modal-actions">
                <button type="button" className="acm-btn acm-btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="acm-btn acm-btn-primary">Create Community</button>
              </div>
            </form>
          </div>
        </div>
      );
    }

    if (modalType === 'editCommunity' && modalData) {
      return (
        <div className="acm-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="acm-modal" onClick={e => e.stopPropagation()}>
            <div className="acm-modal-header">
              <h3>Edit Community</h3>
              <button onClick={() => setShowModal(false)}><Icons.X size={20} /></button>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              handleUpdateCommunity({
                name: formData.get('name'),
                description: formData.get('description'),
                type: formData.get('type'),
                category: formData.get('category'),
                city: formData.get('city'),
                isPublic: formData.get('isPublic') === 'on',
                isFeatured: formData.get('isFeatured') === 'on',
                isActive: formData.get('isActive') === 'on'
              });
            }}>
              <div className="acm-form-group">
                <label>Name *</label>
                <input name="name" required defaultValue={modalData.name} />
              </div>
              <div className="acm-form-group">
                <label>Description</label>
                <textarea name="description" rows={3} defaultValue={modalData.description || ''} />
              </div>
              <div className="acm-form-row">
                <div className="acm-form-group">
                  <label>Type</label>
                  <select name="type" defaultValue={modalData.type}>
                    <option value="MASTERMIND_NICHE">Niche Mastermind</option>
                    <option value="MASTERMIND_CITY">City Mastermind</option>
                    <option value="MASTERMIND_STAGE">Stage Mastermind</option>
                    <option value="BRAND_COMMUNITY">Brand Community</option>
                  </select>
                </div>
                <div className="acm-form-group">
                  <label>Category</label>
                  <input name="category" defaultValue={modalData.category || ''} />
                </div>
              </div>
              <div className="acm-form-group">
                <label>City</label>
                <input name="city" defaultValue={modalData.city || ''} />
              </div>
              <div className="acm-form-group">
                <label>
                  <input type="checkbox" name="isPublic" defaultChecked={modalData.isPublic} /> Public Community
                </label>
              </div>
              <div className="acm-form-group">
                <label>
                  <input type="checkbox" name="isFeatured" defaultChecked={modalData.isFeatured} /> Featured
                </label>
              </div>
              <div className="acm-form-group">
                <label>
                  <input type="checkbox" name="isActive" defaultChecked={modalData.isActive !== false} /> Active
                </label>
              </div>
              <div className="acm-modal-actions">
                <button type="button" className="acm-btn acm-btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="acm-btn acm-btn-primary">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      );
    }

    return null;
  };

  /* =============================================
     MAIN RENDER
     ============================================= */

  // DollarSign icon for payments
  const DollarSignIcon = (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );

  // Wallet icon for payouts
  const WalletIcon = (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" /><path d="M3 5v14a2 2 0 0 0 2 2h16v-5" /><path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
    </svg>
  );

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Icons.BarChart },
    { id: 'communities', label: 'Communities', icon: Icons.Users },
    { id: 'suppliers', label: 'Suppliers', icon: Icons.Package },
    { id: 'moderation', label: 'Moderation', icon: Icons.Flag },
    { id: 'payments', label: 'Revenue', icon: DollarSignIcon },
    { id: 'payouts', label: 'Payouts', icon: WalletIcon },
    { id: 'analytics', label: 'Analytics', icon: Icons.TrendingUp },
    { id: 'settings', label: 'Settings', icon: Icons.Settings }
  ];

  return (
    <div className="acm-container">
      <div className="acm-header">
        <h1>Community Hub</h1>
        <p>Manage communities, suppliers, and moderation</p>
      </div>

      <div className="acm-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`acm-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="acm-error">
          <Icons.AlertTriangle size={18} />
          {error}
          <button onClick={() => setError(null)}><Icons.X size={16} /></button>
        </div>
      )}

      <div className="acm-content">
        {loading && <div className="acm-loading-overlay"><div className="acm-spinner" /></div>}
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'communities' && renderCommunities()}
        {activeTab === 'suppliers' && renderSuppliers()}
        {activeTab === 'moderation' && renderModeration()}
        {activeTab === 'payments' && renderPayments()}
        {activeTab === 'payouts' && renderPayouts()}
        {activeTab === 'analytics' && renderAnalytics()}
        {activeTab === 'settings' && renderSettings()}
      </div>

      {renderModal()}
      {renderTierModal()}
    </div>
  );
};

export default CommunityManagement;
